import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { crearPrestamo, getDataList, getPatronesBasico, getPatronesPrestados } from '../../api/common';
import { useAuth } from '../../hooks/useAuth'
import Swal from 'sweetalert2';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Table,
  Button,
  Spinner,
  Badge,
  InputGroup,
  Tabs,
  Tab
} from 'react-bootstrap';
import { FaSearch, FaCheckCircle, FaSave, FaTimes } from 'react-icons/fa';

const CrearPrestamo = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();

  // Estados
  const [loading, setLoading] = useState(false);
  const [loadingPatrones, setLoadingPatrones] = useState(true);
  const [patrones, setPatrones] = useState([]);

  const [ots, setOts] = useState([]);

  const [equiposSeleccionados, setEquiposSeleccionados] = useState([]);

  // Tabs (Disponibles / Equipos prestados)
  const [tabActiva, setTabActiva] = useState('disponibles');
  const [patronesPrestados, setPatronesPrestados] = useState([]);
  const [loadingPrestados, setLoadingPrestados] = useState(false);
  const [prestadosCargados, setPrestadosCargados] = useState(false);

  // Filtros de tabla
  const [filtros, setFiltros] = useState({
    inventario: '',
    item: '',
    serie: '',
    marca: '',
    ubicacion: ''
  });

  // Datos del formulario
  const [formData, setFormData] = useState({
    ot: '',
    observaciones: ''
  });

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatos();
  }, []);

  // Lazy load de la lista de prestados al abrir la tab por primera vez
  useEffect(() => {
    if (tabActiva === 'prestados' && !prestadosCargados) {
      cargarPrestados();
    }
  }, [tabActiva, prestadosCargados]);

  const cargarPrestados = async () => {
    try {
      setLoadingPrestados(true);
      const resp = await getPatronesPrestados();
      if (resp.status === 200) {
        setPatronesPrestados(resp.data.data || []);
        setPrestadosCargados(true);
      }
    } catch (error) {
      console.error('Error al cargar equipos prestados:', error);
      Swal.fire('Error', 'No se pudieron cargar los equipos prestados', 'error');
    } finally {
      setLoadingPrestados(false);
    }
  };

  const cargarDatos = async () => {
    try {
      setLoadingPatrones(true);
      
      // Cargar patrones disponibles (tipo Prestamo, activos y en Oficina)
      // Usa endpoint /Patrones/basico — sin autopopulate, carga rápida
      const respPatrones = await getPatronesBasico({
        tipoEquipo: 'Prestamo',
        estado: 'Activo',
        movimiento: 'Oficina'
      });

      if (respPatrones.status === 200) {
        setPatrones(respPatrones.data.data || []);
      }

      // Cargar OTs activas
      const respOts = await getDataList('Ot', {
        conditions: {
          otType: 'Parent',
          estado: { $in: ['Solicitud', 'Programada', 'Abierta'] }
        }
      });

      if (respOts.status===200) {
        setOts(respOts.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      Swal.fire('Error', 'No se pudieron cargar los datos', 'error');
    } finally {
      setLoadingPatrones(false);
    }
  };

  // Filtrar patrones según los criterios
  const patronesFiltrados = useMemo(() => {
    return patrones.filter(patron => {
      const cumpleInventario = !filtros.inventario || 
        patron.inventario?.toLowerCase().includes(filtros.inventario.toLowerCase());
      
      const cumpleItem = !filtros.item || 
        patron.item?.toLowerCase().includes(filtros.item.toLowerCase());
      
      const cumpleSerie = !filtros.serie || 
        patron.serie?.toLowerCase().includes(filtros.serie.toLowerCase());
      
      const cumpleMarca = !filtros.marca || 
        patron.marca?.toLowerCase().includes(filtros.marca.toLowerCase());
      
      const cumpleUbicacion = !filtros.ubicacion || 
        patron.ubicacion?.toLowerCase().includes(filtros.ubicacion.toLowerCase());

      return cumpleInventario && cumpleItem && cumpleSerie && cumpleMarca && cumpleUbicacion;
    });
  }, [patrones, filtros]);

  // Manejar cambio en filtros
  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      inventario: '',
      item: '',
      serie: '',
      marca: '',
      ubicacion: ''
    });
  };

  // Manejar selección de equipo
  const handleSeleccionEquipo = (patron) => {
    const yaSeleccionado = equiposSeleccionados.find(
      eq => eq.patron === patron._id
    );

    if (yaSeleccionado) {
      setEquiposSeleccionados(
        equiposSeleccionados.filter(eq => eq.patron !== patron._id)
      );
    } else {
      setEquiposSeleccionados([
        ...equiposSeleccionados,
        {
          patron: patron._id,
          codigo: patron.inventario || '',
          descripcion: patron.item || '',
          serie: patron.serie || '',
          ubicacionAnterior: patron.ubicacion || patron.movimiento || '',
          estado: 'prestado',
          snapshotEquipo: {
            item: patron.item || '',
            marca: patron.marca || '',
            modelo: patron.modelo || '',
            serie: patron.serie || '',
            inventario: patron.inventario || ''
          }
        }
      ]);
    }
  };

  // Seleccionar/Deseleccionar todos los filtrados (excluye los que están en préstamo)
  const handleSeleccionarTodos = () => {
    const disponibles = patronesFiltrados.filter(p => p.movimiento !== 'Préstamo');
    const todosSeleccionados = disponibles.every(p => equiposSeleccionados.some(eq => eq.patron === p._id));
    if (todosSeleccionados) {
      setEquiposSeleccionados([]);
    } else {
      const nuevosSeleccionados = disponibles.map(patron => ({
        patron: patron._id,
        codigo: patron.inventario || '',
        descripcion: patron.item || '',
        serie: patron.serie || '',
        ubicacionAnterior: patron.ubicacion || patron.movimiento || '',
        estado: 'prestado',
        snapshotEquipo: {
          item: patron.item || '',
          marca: patron.marca || '',
          modelo: patron.modelo || '',
          serie: patron.serie || '',
          inventario: patron.inventario || ''
        }
      }));
      setEquiposSeleccionados(nuevosSeleccionados);
    }
  };

  // Verificar si un patrón está seleccionado
  const estaSeleccionado = (patronId) => {
    return equiposSeleccionados.some(eq => eq.patron === patronId);
  };

  // Manejar cambio en formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validar formulario
  const validarFormulario = () => {
    if (!formData.ot) {
      Swal.fire('Validación', 'Debe seleccionar una OT', 'warning');
      return false;
    }

    if (equiposSeleccionados.length === 0) {
      Swal.fire('Validación', 'Debe seleccionar al menos un equipo', 'warning');
      return false;
    }

    return true;
  };

  // Crear préstamo
  const handleCrearPrestamo = async () => {
    if (!validarFormulario()) return;

    try {
      setLoading(true);

      const otSeleccionada = ots.find(ot => ot._id === formData.ot);

      const datosPrestamo = {
        ot: formData.ot,
        numeroOt: otSeleccionada?.NumeroOt || '',
        usuarioCreacion: userData._id,
        nombreUsuarioCreacion: userData.nombre ? `${userData.nombre} ${userData.apellido || ''}`.trim() : '',
        equipos: equiposSeleccionados,
        observaciones: formData.observaciones,
        estado: 'activo',
        activo: true
      };

      const resp = await crearPrestamo(datosPrestamo);
      console.log('Respuesta creación préstamo:', resp);

      if (resp.status === 201 ) {
        Swal.fire({
          icon: 'success',
          title: '¡Préstamo creado!',
          text: `Acta ${resp.data.numeroActa} creada exitosamente`,
          confirmButtonText: 'Aceptar'
        }).then(() => {
          navigate('/prestamos');
        });
      } else {
        Swal.fire('Error', resp.message || 'No se pudo crear el préstamo', 'error');
      }
    } catch (error) {
      console.error('Error al crear préstamo:', error);
      Swal.fire('Error', 'Ocurrió un error al crear el préstamo', 'error');
    } finally {
      setLoading(false);
    }
  };

   const toProperCase = (text) => {
  return text
    .toLowerCase() // primero convierte todo a minúsculas
    .split(' ')    // separa por espacios
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // capitaliza la primera letra
    .join(' ');    // une las palabras de nuevo
}

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-0">
            <FaCheckCircle className="me-2 text-primary" />
            Crear Acta de Préstamo
          </h2>
          <p className="text-muted">Registra el préstamo de equipos patrón a una OT</p>
        </Col>
      </Row>

      <Tabs
        activeKey={tabActiva}
        onSelect={(k) => setTabActiva(k)}
        className="mb-3"
      >
        <Tab eventKey="disponibles" title="Disponibles">
      <Row>
        {/* Formulario Principal */}
        <Col lg={4}>
          <Card className="shadow-sm mb-4 w-100">
            <Card.Header className="bg-primary text-white w-100">
              <h5 className="mb-0">Información del Préstamo</h5>
            </Card.Header>
            <Card.Body className="w-100">
              <Form>
                {/* OT */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    Orden de Trabajo <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="ot"
                    value={formData.ot}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccione OT...</option>
                    {ots.map(ot => (
                      <option key={ot._id} value={ot._id}>
                        {ot.numeroOrden} - { toProperCase(ot.cliente?.nombre)  || 'Sin cliente'}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Usuario */}
                <Form.Group className="mb-3">
                  <Form.Label>Creado por</Form.Label>
                  <Form.Control
                    type="text"
                    value={`${userData?.nombre || ''} ${userData?.apellido || ''}`}
                    disabled
                  />
                </Form.Group>

                {/* Observaciones */}
                <Form.Group className="mb-3">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="observaciones"
                    value={formData.observaciones}
                    onChange={handleInputChange}
                    placeholder="Observaciones adicionales..."
                  />
                </Form.Group>

                {/* Equipos seleccionados */}
                <div className="mb-3">
                  <strong>Equipos seleccionados:</strong>
                  <Badge bg="info" className="ms-2">
                    {equiposSeleccionados.length}
                  </Badge>
                </div>

                {/* Botones */}
                <div className="d-grid gap-2">
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleCrearPrestamo}
                    disabled={loading || equiposSeleccionados.length === 0}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Creando préstamo...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Crear Préstamo
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/prestamos')}
                    disabled={loading}
                  >
                    <FaTimes className="me-2" />
                    Cancelar
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Tabla de Selección de Equipos */}
        <Col lg={8}>
          <Card className="shadow-sm mb-4 w-100">
            <Card.Header className="bg-white w-100">
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">Seleccionar Equipos Patrón</h5>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleSeleccionarTodos}
                  >
                    {equiposSeleccionados.length === patronesFiltrados.length
                      ? 'Deseleccionar Todos'
                      : 'Seleccionar Todos'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-2"
                    onClick={limpiarFiltros}
                  >
                    Limpiar Filtros
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0 w-100">
              {loadingPatrones ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Cargando equipos...</p>
                </div>
              ) : (
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <Table striped hover responsive className="mb-0">
                    <thead className="bg-light sticky-top">
                      <tr>
                        <th style={{ width: '50px' }} className="text-center">
                          <div className="mb-2">Selec...</div>
                        </th>
                        <th>
                          <div className="mb-2">Inventario</div>
                          <InputGroup size="sm">
                            <InputGroup.Text>
                              <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Filtrar..."
                              value={filtros.inventario}
                              onChange={(e) =>
                                handleFiltroChange('inventario', e.target.value)
                              }
                            />
                          </InputGroup>
                        </th>
                        <th>
                          <div className="mb-2">Descripción/Item</div>
                          <InputGroup size="sm">
                            <InputGroup.Text>
                              <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Filtrar..."
                              value={filtros.item}
                              onChange={(e) =>
                                handleFiltroChange('item', e.target.value)
                              }
                            />
                          </InputGroup>
                        </th>
                        <th>
                          <div className="mb-2">Serie</div>
                          <InputGroup size="sm">
                            <InputGroup.Text>
                              <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Filtrar..."
                              value={filtros.serie}
                              onChange={(e) =>
                                handleFiltroChange('serie', e.target.value)
                              }
                            />
                          </InputGroup>
                        </th>
                        <th>
                          <div className="mb-2">Marca</div>
                          <InputGroup size="sm">
                            <InputGroup.Text>
                              <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Filtrar..."
                              value={filtros.marca}
                              onChange={(e) =>
                                handleFiltroChange('marca', e.target.value)
                              }
                            />
                          </InputGroup>
                        </th>
                        <th>
                          <div className="mb-2">Ubicación</div>
                          <InputGroup size="sm">
                            <InputGroup.Text>
                              <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Filtrar..."
                              value={filtros.ubicacion}
                              onChange={(e) =>
                                handleFiltroChange('ubicacion', e.target.value)
                              }
                            />
                          </InputGroup>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {patronesFiltrados.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <p className="text-muted mb-0">
                              No hay equipos disponibles con los filtros aplicados
                            </p>
                          </td>
                        </tr>
                      ) : (
                        patronesFiltrados.map((patron) => {
                          const estaPrestado = patron.movimiento === 'Préstamo';
                          return (
                            <tr
                              key={patron._id}
                              className={
                                estaPrestado
                                  ? 'table-secondary text-muted'
                                  : estaSeleccionado(patron._id)
                                  ? 'table-primary'
                                  : ''
                              }
                              style={{
                                cursor: estaPrestado ? 'not-allowed' : 'pointer',
                                opacity: estaPrestado ? 0.6 : 1
                              }}
                              onClick={() => !estaPrestado && handleSeleccionEquipo(patron)}
                            >
                              <td className="text-center">
                                <Form.Check.Input
                                  type="checkbox"
                                  style={{ position: 'static', transform: 'scale(1.4)', cursor: estaPrestado ? 'not-allowed' : 'pointer' }}
                                  checked={estaSeleccionado(patron._id)}
                                  disabled={estaPrestado}
                                  onChange={() => !estaPrestado && handleSeleccionEquipo(patron)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td><strong>{patron.inventario}</strong></td>
                              <td>{patron.item}</td>
                              <td>{patron.serie}</td>
                              <td>{patron.marca}</td>
                              <td>
                                {estaPrestado ? (
                                  <Badge bg="warning" text="dark">Equipo Prestado</Badge>
                                ) : (
                                  <Badge bg="secondary">{patron.ubicacion || patron.movimiento}</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            <Card.Footer className="bg-light text-center w-100">
              <small className="text-muted">
                Mostrando {patronesFiltrados.length} de {patrones.length} equipos disponibles
              </small>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
        </Tab>

        <Tab eventKey="prestados" title="Equipos prestados">
          <Card className="shadow-sm mb-4 w-100">
            <Card.Header className="bg-warning text-dark w-100">
              <h5 className="mb-0">Equipos actualmente prestados</h5>
            </Card.Header>
            <Card.Body className="p-0 w-100">
              {loadingPrestados ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Cargando equipos prestados...</p>
                </div>
              ) : patronesPrestados.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted mb-0">No hay equipos en préstamo actualmente</p>
                </div>
              ) : (
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <Table striped hover responsive className="mb-0">
                    <thead className="bg-light sticky-top">
                      <tr>
                        <th>Inventario</th>
                        <th>Item</th>
                        <th>Marca</th>
                        <th>Serie</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patronesPrestados.map((p) => {
                        const tienePrestamo = !!p.ultimoPrestamo;
                        return (
                          <tr
                            key={p._id}
                            style={{ cursor: tienePrestamo ? 'pointer' : 'not-allowed' }}
                            onClick={() =>
                              tienePrestamo && navigate(`/prestamos/detalle/${p.ultimoPrestamo}`)
                            }
                            title={
                              tienePrestamo
                                ? 'Ver detalle del préstamo'
                                : 'Sin préstamo asociado'
                            }
                          >
                            <td><strong>{p.inventario}</strong></td>
                            <td>
                              {p.item}
                              {!tienePrestamo && (
                                <Badge bg="secondary" className="ms-2">Sin préstamo asociado</Badge>
                              )}
                            </td>
                            <td>{p.marca}</td>
                            <td>{p.serie}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            <Card.Footer className="bg-light text-center w-100">
              <small className="text-muted">
                Total: {patronesPrestados.length} equipo(s) en préstamo
              </small>
            </Card.Footer>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default CrearPrestamo;